import { ReactNode, useCallback } from 'react';
import { toast } from 'sonner';

import { Snackbar, SnackbarVariant } from '../components/Snackbar';
import { useTranslation } from '../i18n';

export type { SnackbarVariant };

interface SnackbarOptions {
  variant: SnackbarVariant;
  persist?: boolean;
  autoHideDuration?: number;
  key?: string;
  action?: ReactNode;
  loading?: boolean;
  hideCloseButton?: boolean;
}

export function useSnackbar() {
  const { t } = useTranslation();

  const enqueueSnackbar = useCallback(
    (message: string | undefined | null, options: SnackbarOptions) => {
      const defaultMessage = options.variant === 'error' ? t('common.generic_error_message') : '';
      const finalMessage = message || defaultMessage;
      const duration = options.persist ? Infinity : (options.autoHideDuration ?? 3000);
      const toastOptions = options.key ? { duration, id: options.key } : { duration };

      return toast.custom(
        toastId => (
          <Snackbar
            id={toastId}
            variant={options.variant}
            message={finalMessage}
            action={options.action}
            loading={options.loading}
            onClose={options.hideCloseButton ? undefined : () => toast.dismiss(toastId)}
          />
        ),
        toastOptions
      );
    },
    [t]
  );

  const closeSnackbar = useCallback((id?: string | number) => {
    if (id !== undefined) {
      toast.dismiss(id);
    } else {
      toast.dismiss();
    }
  }, []);

  return {
    enqueueSnackbar,
    closeSnackbar,
  };
}
